import React, { useState, useEffect, useMemo, useContext } from "react";
import { Modal } from "reactstrap";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  LuX,
  LuCheck,
  LuCalendar,
  LuClock,
  LuGlobe,
  LuVideo,
  LuFileText,
  LuExternalLink,
  LuMessageSquare,
  LuFile,
  LuArrowRight,
  LuAward,
} from "react-icons/lu";
import { PiFilePdf } from "react-icons/pi";
import { CHAT } from "../../constants/RoutesUrl";
import { apiAdmin } from "../../services/api";
import { resolveAgendaImageUrl } from "../../utils/agendaUtils";
import { Language } from "../../App";
import placeholderAvatar from "../../assets/images/icon/placeholder_Awatar.png";
import {
  Calendar3DIcon,
  Clock3DIcon,
  Video3DIcon,
  Check3DIcon,
} from "./common/Agenda3DIcons";
import "./AgendaCompletedMentorshipModal.css";

const AgendaCompletedMentorshipModal = ({
  isOpen,
  toggle,
  event = null,
  lang: propLang,
}) => {
  const { lang: contextLang } = useContext(Language) || {};
  const lang = propLang || contextLang;
  const navigate = useNavigate();

  const [detailedSession, setDetailedSession] = useState(null);

  // Extract raw and meta data
  const raw = event?.raw || event || {};
  let rawMeta = raw.meta || event?.meta || {};
  if (typeof rawMeta === "string") {
    try {
      rawMeta = JSON.parse(rawMeta);
    } catch (e) {
      rawMeta = {};
    }
  }

  // Current logged in user ID
  const currentUserId = useMemo(() => {
    try {
      const signupData = JSON.parse(localStorage.getItem("SignupData") || "null");
      const splashData = JSON.parse(localStorage.getItem("SplashData") || "null");
      return signupData?.data?.id || splashData?.user?.id || null;
    } catch (e) {
      return null;
    }
  }, []);

  // Clean mentorship ID for detail query
  const cleanMentorshipId = useMemo(() => {
    return String(
      raw.id || rawMeta.request_id || event?.apiId || event?.raw_id || event?.id || ""
    ).replace(/^(?:mentorship-|mentor-|session-|evt-)/i, "");
  }, [raw, rawMeta, event]);

  // Fetch full activity detail if needed (to hydrate CV, user notes, duration, etc.)
  useEffect(() => {
    if (!isOpen || !cleanMentorshipId) return;

    let isMounted = true;
    apiAdmin
      .post("user/mentor/activity", { type: "mentor", status: "completed" })
      .then((res) => {
        const list = res?.data?.data?.data || [];
        let found = list.find((a) => String(a.id) === String(cleanMentorshipId));
        if (found) return found;

        return apiAdmin
          .post("user/mentor/activity", { type: "mentee", status: "completed" })
          .then((res2) => {
            const list2 = res2?.data?.data?.data || [];
            return list2.find((a) => String(a.id) === String(cleanMentorshipId));
          });
      })
      .then((foundItem) => {
        if (isMounted && foundItem) {
          setDetailedSession(foundItem);
        }
      })
      .catch((err) => {
        console.warn("Could not fetch completed mentorship activity:", err);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, cleanMentorshipId]);

  // Merge base event with asynchronously fetched details
  const sessionData = useMemo(() => {
    return {
      ...raw,
      ...(detailedSession || {}),
      meta: {
        ...rawMeta,
        ...(detailedSession?.meta || {}),
      },
    };
  }, [raw, rawMeta, detailedSession]);

  // 1. Mentor details
  const mentorObj = sessionData.mentor || event?.mentor || {};
  const mentorId = mentorObj.id || rawMeta.mentor_id || sessionData.mentor_id;
  const mentorName =
    mentorObj.name ||
    [mentorObj.first_name, mentorObj.last_name].filter(Boolean).join(" ") ||
    event?.host ||
    "Dr. Specialist Mentor";
  const mentorSpecialty =
    mentorObj.user_title ||
    mentorObj.sub_type ||
    mentorObj.category ||
    mentorObj.specialty ||
    event?.hostRole ||
    "Specialist Doctor";
  const mentorAvatar = resolveAgendaImageUrl(
    mentorObj.image || mentorObj.thumbnail || mentorObj.avatar || event?.hostAvatar,
    placeholderAvatar
  );

  // 2. Mentee details
  const menteeObj = sessionData.user || event?.user || {};
  const menteeId = menteeObj.id || sessionData.user_id || event?.user_id;
  const menteeName =
    menteeObj.name ||
    [menteeObj.first_name, menteeObj.last_name].filter(Boolean).join(" ") ||
    "Doctor / Mentee";
  const menteeSpecialty =
    menteeObj.user_title ||
    menteeObj.sub_type ||
    menteeObj.category ||
    menteeObj.specialty ||
    "Resident / Fellow";
  const menteeAvatar = resolveAgendaImageUrl(
    menteeObj.image || menteeObj.thumbnail || menteeObj.avatar,
    placeholderAvatar
  );

  // 3. User Role & Counterpart for Chat
  const isMentorUser = Boolean(
    currentUserId && mentorId && String(currentUserId) === String(mentorId)
  );
  const counterpartName = isMentorUser ? menteeName : mentorName;
  const counterpartId = isMentorUser ? menteeId : mentorId;
  const counterpartAvatar = isMentorUser ? menteeAvatar : mentorAvatar;
  const counterpartTitle = isMentorUser ? menteeSpecialty : mentorSpecialty;

  // 4. Session Date & Time
  const sessionDate =
    sessionData.start_date ||
    sessionData.date ||
    event?.date ||
    "";
  const sessionTime =
    sessionData.start_time ||
    sessionData.time ||
    event?.startTime ||
    event?.time ||
    "";
  const duration =
    sessionData.duration ||
    event?.duration ||
    15;
  const timezone =
    sessionData.timezone ||
    rawMeta.timezone ||
    event?.timezone ||
    sessionStorage.getItem("user_timezone") ||
    "UTC";

  // Formatted date string
  const displayFormattedDate = useMemo(() => {
    const rawDate = sessionDate || sessionData.start_timestamp || event?.start_timestamp;
    if (!rawDate) return lang?.date_not_specified || "Date not specified";
    try {
      if (typeof rawDate === "number" || (typeof rawDate === "string" && /^\d{10,}$/.test(rawDate.trim()))) {
        const m = moment.unix(Number(rawDate));
        if (m.isValid()) return m.format("ddd, MMM D, YYYY");
      }
      const m = moment(rawDate);
      if (m.isValid()) return m.format("ddd, MMM D, YYYY");
    } catch (e) {}
    return String(rawDate);
  }, [sessionDate, sessionData.start_timestamp, event?.start_timestamp, lang]);

  // 5. CV & Attached Documents
  const cvPath = sessionData.data || sessionData.cv || rawMeta.cv || "";
  const cvUrl = cvPath ? resolveAgendaImageUrl(cvPath, null) : null;
  const cvFileName = cvPath ? cvPath.split("/").pop() : "";

  // 6. Discussion Notes / Message
  const sessionNotes =
    sessionData.message ||
    sessionData.notes ||
    sessionData.reason ||
    sessionData.description ||
    event?.description ||
    "";

  // Open Chat with Counterpart
  const handleOpenChat = () => {
    if (!counterpartId) return;
    toggle();
    navigate(CHAT, {
      state: {
        from: currentUserId,
        to: counterpartId,
        user_id: counterpartId,
        group_id: 0,
        user: {
          id: counterpartId,
          user_id: counterpartId,
          name: counterpartName,
          image: counterpartAvatar,
          user_title: counterpartTitle,
        },
        selectedUser: {
          id: counterpartId,
          name: counterpartName,
          image: counterpartAvatar,
          user_title: counterpartTitle,
        },
      },
    });
  };

  if (!event) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="agenda-completed-mentorship-modal"
    >
      {/* Header Banner */}
      <div className="completed-mentorship-header">
        <div className="completed-mentorship-header-top">
          <div className="completed-mentorship-badges">
            <span className="completed-mentorship-type-badge">
              <LuAward size={13} style={{ color: "#38BDF8" }} />
              <span>{lang?.mentorship || "Mentorship Session"}</span>
            </span>
            <span className="completed-mentorship-status-pill">
              <LuCheck size={13} strokeWidth={2.5} />
              <span>{lang?.completed || "Completed"}</span>
            </span>
          </div>

          <button
            type="button"
            className="completed-mentorship-close-btn"
            onClick={toggle}
            aria-label="Close"
          >
            <LuX size={18} />
          </button>
        </div>

        <h3 className="completed-mentorship-title">
          {event?.title || lang?.mentorship_session_completed || "Mentorship Session Completed"}
        </h3>
        <p className="completed-mentorship-subtitle">
          {lang?.completed_session_summary || "1-on-1 private medical consultation & career guidance session"}
        </p>
      </div>

      {/* Modal Content Body */}
      <div className="completed-mentorship-body">
        {/* Mentor & Mentee Participant Connecting Card */}
        <div className="completed-mentorship-participants">
          {/* Mentor (Left) */}
          <div className="participant-card mentor">
            <div className="participant-avatar-wrap">
              <img src={mentorAvatar} alt={mentorName} />
            </div>
            <div className="participant-info">
              <span className="participant-tag tag-mentor">
                {lang?.mentor || "Mentor"}
              </span>
              <h5 className="participant-name" title={mentorName}>
                {mentorName}
              </h5>
              <p className="participant-title" title={mentorSpecialty}>
                {mentorSpecialty}
              </p>
            </div>
          </div>

          {/* 1-on-1 Connector */}
          <div className="participants-connector">
            <div className="connector-circle" title="1-on-1 Session">
              1:1
            </div>
          </div>

          {/* Mentee (Right) */}
          <div className="participant-card mentee">
            <div className="participant-avatar-wrap">
              <img src={menteeAvatar} alt={menteeName} />
            </div>
            <div className="participant-info">
              <span className="participant-tag tag-mentee">
                {lang?.mentee || "Mentee"}
              </span>
              <h5 className="participant-name" title={menteeName}>
                {menteeName}
              </h5>
              <p className="participant-title" title={menteeSpecialty}>
                {menteeSpecialty}
              </p>
            </div>
          </div>
        </div>

        {/* 2x2 Session Details Grid */}
        <div className="completed-mentorship-grid">
          {/* Date */}
          <div className="completed-grid-cell">
            <div className="completed-grid-icon">
              <Calendar3DIcon size={26} />
            </div>
            <div className="completed-grid-content">
              <div className="completed-grid-label">
                {lang?.date || "Date"}
              </div>
              <div className="completed-grid-value" title={displayFormattedDate}>
                {displayFormattedDate}
              </div>
            </div>
          </div>

          {/* Time & Duration */}
          <div className="completed-grid-cell">
            <div className="completed-grid-icon">
              <Clock3DIcon size={26} />
            </div>
            <div className="completed-grid-content">
              <div className="completed-grid-label">
                {lang?.time_and_duration || "Time & Duration"}
              </div>
              <div className="completed-grid-value" title={`${sessionTime || "11:00 AM"} • ${duration} Mins`}>
                {sessionTime ? `${sessionTime} • ` : ""}{duration} Mins
              </div>
            </div>
          </div>

          {/* Mode & Timezone */}
          <div className="completed-grid-cell">
            <div className="completed-grid-icon">
              <Video3DIcon size={26} />
            </div>
            <div className="completed-grid-content">
              <div className="completed-grid-label">
                {lang?.session_mode || "Format"}
              </div>
              <div className="completed-grid-value" title="Online HD Room">
                {lang?.online_video_call || "Online Video Call"}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="completed-grid-cell">
            <div className="completed-grid-icon">
              <Check3DIcon size={26} />
            </div>
            <div className="completed-grid-content">
              <div className="completed-grid-label">
                {lang?.status || "Status"}
              </div>
              <div className="completed-grid-value" style={{ color: "#059669" }}>
                {lang?.completed || "Completed"}
              </div>
            </div>
          </div>
        </div>

        {/* Attached Document / CV (if available) */}
        {cvUrl && (
          <div className="completed-mentorship-doc-card">
            <div className="completed-doc-left">
              <div className="completed-doc-icon-wrap">
                <PiFilePdf size={22} />
              </div>
              <div className="completed-doc-info">
                <h5 className="completed-doc-title" title={cvFileName || "Mentee_CV.pdf"}>
                  {cvFileName || "Mentee_CV.pdf"}
                </h5>
                <p className="completed-doc-sub">
                  {lang?.attached_document || "Attached Mentorship Document"}
                </p>
              </div>
            </div>
            <a
              href={cvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="completed-doc-btn"
            >
              <span>{lang?.view_file || "View"}</span>
              <LuExternalLink size={13} />
            </a>
          </div>
        )}

        {/* Session Discussion Notes / Overview (if available) */}
        {sessionNotes && (
          <div className="completed-mentorship-notes-card">
            <div className="completed-notes-heading">
              <LuFileText size={14} style={{ color: "#00BADA" }} />
              <span>{lang?.session_notes || "Session Topic & Notes"}</span>
            </div>
            <p className="completed-notes-text">
              {sessionNotes}
            </p>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="completed-mentorship-footer">
        <button
          type="button"
          className="completed-mentorship-btn-close"
          onClick={toggle}
        >
          {lang?.close || "Close"}
        </button>

        {counterpartId && (
          <button
            type="button"
            className="completed-mentorship-btn-chat"
            onClick={handleOpenChat}
          >
            <LuMessageSquare size={16} />
            <span>
              {isMentorUser
                ? (lang?.message_mentee || "Message Mentee")
                : (lang?.message_mentor || "Message Mentor")}
            </span>
          </button>
        )}
      </div>
    </Modal>
  );
};

export default AgendaCompletedMentorshipModal;
